import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import {
  AddFormComponent,
  FormField,
} from '@shared/components/add-form/add-form.component';
import { ProviderFormConfig } from '../../e-invoice-provider.config';
import {
  EInvoiceProvider,
  EInvoiceProviderCreateRequest,
  EInvoiceProviderService,
} from '../../services/e-invoice-provider.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';

@Component({
  selector: 'app-provider-form-wrapper',
  standalone: true,
  imports: [CommonModule, ToastModule, AddFormComponent, TranslateModule],
  providers: [MessageService],
  templateUrl: './provider-form-wrapper.component.html',
  styleUrls: ['./provider-form-wrapper.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProviderFormWrapperComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly providerService = inject(EInvoiceProviderService);
  private readonly messageService = inject(MessageService);
  private readonly errorDialogService = inject(ErrorDialogService);
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @Input() config!: ProviderFormConfig;
  @Input() editMode = false;
  @Input() viewMode = false;
  @ViewChild('addForm') addFormComponent?: AddFormComponent;

  // Data signals
  loading = signal(false);
  initialLoading = signal(true);
  formFields = signal<FormField[]>([]);
  formData = signal<any>({});
  providerId = signal<number | null>(null);
  private skipCanDeactivate = false;

  ngOnInit(): void {
    this.initializeFormFields();
    this.loadProviderData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFormFields(): void {
    const fields = this.config.formFields.map((field) => ({ ...field }));
    this.updateFieldReadonly(fields);
    this.formFields.set(fields);
  }

  private loadProviderData(): void {
    if (!this.editMode && !this.viewMode) {
      this.initialLoading.set(false);
      return;
    }

    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('common.messages.invalid_id'),
      });
      this.router.navigate([this.config.listRoute]);
      return;
    }

    const providerId = parseInt(id, 10);
    if (isNaN(providerId)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('common.messages.invalid_id'),
      });
      this.router.navigate([this.config.listRoute]);
      return;
    }

    this.providerId.set(providerId);
    this.loading.set(true);

    this.providerService
      .getProviderById(providerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (provider: EInvoiceProvider) => {
          const formData = this.transformProviderToFormData(provider);
          this.formData.set(formData);
          const fields = [...this.formFields()];
          this.updateFieldReadonly(fields);
          this.formFields.set(fields);
          this.loading.set(false);
          this.initialLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading.set(false);
          this.initialLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'e_invoice.provider.messages.error.failed_to_load_provider',
            ),
          });
          this.router.navigate([this.config.listRoute]);
        },
      });
  }

  private transformProviderToFormData(provider: EInvoiceProvider): any {
    return {
      providerName: provider.providerName,
      providerCode: provider.providerCode,
      note: provider.note,
      status: provider.status,
    };
  }

  private updateFieldReadonly(fields: FormField[]): void {
    if (this.editMode) {
      fields.forEach((field) => {
        if (field.controlName === 'providerCode') {
          field.readonly = true;
        }
      });
    }
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'cancel') {
      this.router.navigate([this.config.listRoute]);
      return;
    }

    if (event.action === 'save') {
      this.handleSave(event.data);
      return;
    }
  }

  canDeactivate(): boolean {
    if (this.viewMode || this.skipCanDeactivate || this.loading()) {
      return true;
    }
    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  private handleSave(formData: any): void {
    if (!formData) return;

    const payload = this.transformFormDataToPayload(formData);
    this.loading.set(true);

    if (this.editMode && this.providerId()) {
      this.providerService
        .updateProvider(this.providerId()!, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loading.set(false);
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'e_invoice.provider.messages.success.updated',
              ),
            });
            this.router.navigate([this.config.listRoute]);
          },
          error: (error) => {
            this.loading.set(false);
            const fallbackKey =
              'e_invoice.provider.messages.error.failed_to_update';
            this.errorDialogService.showError(
              this.errorTranslatorService.translate(
                error.error?.message,
                fallbackKey,
              ),
            );
          },
        });
    } else {
      this.providerService
        .createProvider(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loading.set(false);
            this.skipCanDeactivate = true;
            this.addFormComponent?.form?.markAsPristine();
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'e_invoice.provider.messages.success.created',
              ),
            });
            this.router.navigate([this.config.listRoute]);
          },
          error: (error) => {
            this.loading.set(false);
            const fallbackKey =
              'e_invoice.provider.messages.error.failed_to_create';
            this.errorDialogService.showError(
              this.errorTranslatorService.translate(
                error.error?.message,
                fallbackKey,
              ),
            );
          },
        });
    }
  }

  private transformFormDataToPayload(
    formData: any,
  ): EInvoiceProviderCreateRequest {
    return {
      providerName: formData.providerName,
      providerCode: formData.providerCode,
      note: formData.note || undefined,
      status: !!formData.status,
    };
  }
}
